import { capitalize, startCase } from 'lodash'
import { DownOutlined } from '@ant-design/icons'
import { Dropdown } from 'antd'
import { Flex, Box, Heading } from '@weareberyl/design-system'

import { formatDatetime } from '~/utils'
import { VehicleContextMenu } from '~/components/Module/context'
import ModuleHeader from '~/components/ModuleHeader'
import Tags from '../../Module/Tags'
import CardSection from '~/components/shared/CardSection'
import LegendCard from './Card'

import { assistConfig } from '~/components/Module/Summary'
import {
  ModuleHardware,
  useVehicleLegendQuery,
  FrameType,
} from '~/gql/generated/graphql'
import { REWARD_TYPES } from '~/constants/domain'

export interface VehicleLegendProps {
  module_id: string
  onClose: () => void
  where?: object | null
}

const VehicleLegend = ({ module_id, onClose }: VehicleLegendProps) => {
  const { data } = useVehicleLegendQuery({ variables: { module_id } })
  const vehicle = data?.vehicle_by_module_id
  const detail = vehicle?.detail
  const module = detail?.module

  const message =
    module?.lock_state?.vehicle_lock_state ?? module?.lock_state?.overall_state
  const availability = module?.availability?.status
  const maxError = module?.location?.max_error
  const batteryPercent = module?.telemetry?.drive?.battery_percent
  const moduleState = module?.module_state
  const powerMode =
    (moduleState && 'power_mode' in moduleState
      ? moduleState.power_mode
      : null) ?? 'No data'
  const time = module?.location?.time
  const frame_type = vehicle?.frame_type
  const hardware_type = vehicle?.hardware_type
  const vehicleId = vehicle?.id

  const isOnline = module?.connectivity?.is_online ?? 'No data'
  const connected =
    isOnline !== 'No data' ? (isOnline ? 'Online' : 'Offline') : isOnline
  const scheme = vehicle?.detail?.scheme
  const rewards = vehicle?.rewards ?? []
  const lock_configuration = vehicle?.detail?.lock_configuration

  const legendItems = [
    {
      key: 'Module ID',
      value: module_id,
    },
    {
      key: 'Frame Type',
      value: startCase(frame_type),
    },
    {
      key: 'Module ID',
      value: module_id,
    },
    {
      key: 'Lock state',
      value: message ? capitalize(message) : 'No data',
    },
    {
      key: 'Connected',
      value: connected,
    },
    {
      key: 'Availability',
      value: availability ? capitalize(availability) : 'No data',
    },
    {
      key: 'Max error',
      value: maxError ? `${maxError}m` : 'No data',
    },
    {
      key: 'Module charge',
      value: batteryPercent ? `${batteryPercent}%` : 'No data',
    },
    {
      key: 'Power mode',
      value: capitalize(powerMode),
    },
  ]

  if (module?.module_state && 'assist_config' in module.module_state) {
    if (hardware_type === ModuleHardware.bbe) {
      legendItems.push({
        key: 'Assist config',
        value:
          (module?.module_state?.assist_config &&
            assistConfig[module?.module_state?.assist_config]) ||
          'No data',
      })
    }
  }

  legendItems.push({
    key: 'Data received',
    value: time ? formatDatetime(time) : 'No data',
  })

  if (rewards.length > 0) {
    const rewardsString =
      rewards.length > 0
        ? rewards.map(reward => `⭐️ ${REWARD_TYPES[reward.type]}`).join(', ')
        : 'None'

    legendItems.push({
      key: 'Rewards available',
      value: rewardsString,
    })
  }

  if (!hardware_type || !vehicleId) {
    return null
  }
  return (
    <LegendCard
      onClose={onClose}
      footer={{
        custom: (
          <Dropdown
            key="dropdown"
            trigger={['click']}
            overlay={
              <VehicleContextMenu
                module_id={module_id}
                hardware_type={hardware_type}
                vehicleId={vehicleId}
                lock_configuration={lock_configuration}
              />
            }
          >
            <Flex justifyContent="space-between" style={{ cursor: 'pointer' }}>
              <Heading variant="h4">More</Heading>
              <DownOutlined style={{ fontSize: 18, paddingTop: 2 }} />
            </Flex>
          </Dropdown>
        ),
      }}
    >
      <Box flex="1" mb={3}>
        <Box mt={2} mb={3}>
          <ModuleHeader
            moduleId={module_id}
            frameId={vehicle.frame_id}
            hardwareType={hardware_type}
            frameType={vehicle?.frame_type as FrameType}
            batteryPercent={module?.telemetry?.drive?.battery_percent ?? null}
            scheme={scheme}
            size="medium"
          />
        </Box>
        <Tags vehicleId={vehicleId} tags={module?.tags ?? []} />
        <Flex mt={4}>
          <CardSection items={legendItems} />
        </Flex>
      </Box>
    </LegendCard>
  )
}

export default VehicleLegend
